/**
 * Utility functions for detecting mobile devices and touch capabilities
 */

/**
 * Detects if the current device is a mobile device
 * Uses multiple detection methods for better accuracy
 */
export const isMobileDevice = (): boolean => {
  // Check for touch capability
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Check user agent for mobile indicators
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'android', 'webos', 'iphone', 'ipad', 'ipod', 
    'blackberry', 'iemobile', 'opera mini', 'mobile'
  ];
  const hasMobileUserAgent = mobileKeywords.some(keyword => userAgent.includes(keyword));
  
  // Check screen size (mobile devices typically have smaller screens)
  const hasSmallScreen = window.screen.width <= 768 || window.screen.height <= 768;
  
  // Check for mobile-specific APIs
  const hasMobileAPIs = 'orientation' in window || 'DeviceOrientationEvent' in window;
  
  // Device is considered mobile if it has touch AND (mobile user agent OR small screen OR mobile APIs)
  return hasTouch && (hasMobileUserAgent || hasSmallScreen || hasMobileAPIs);
};

/**
 * Detects if the device supports orientation changes
 */
export const supportsOrientationChange = (): boolean => {
  if (!isMobileDevice()) {
    return false;
  }
  
  // Check for orientation support
  if ('orientation' in window || 'DeviceOrientationEvent' in window) {
    return true;
  }
  
  // Check for screen orientation API with proper type casting
  try {
    const screen = (window as any).screen;
    return 'screen' in window && screen && 'orientation' in screen;
  } catch {
    return false;
  }
};

/**
 * Detects if the device is likely a tablet
 */
export const isTabletDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const tabletKeywords = ['ipad', 'tablet', 'kindle'];
  const hasTabletUserAgent = tabletKeywords.some(keyword => userAgent.includes(keyword));
  
  // Large touch screen (likely tablet)
  const hasLargeScreen = window.screen.width >= 768 && window.screen.height >= 1024;
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return hasTabletUserAgent || (hasTouch && hasLargeScreen);
};
